import { get, map, mapValues } from 'lodash'
import { TFunction } from 'i18next'
import { deleteReq, getReq, postReq, putReq } from '../../utils/request'
import { CREATE_COMPLAINT, LOAD_COMPLAINT, LOAD_SELECT_COMPLAINTS } from './types'
import { IResponsePagination } from '../../types/interfaces'
import { ThunkResult } from '../index'

export enum COMPLAINT_STATUS {
	UNJUSTIFIED = 'unjustified',
	JUSTIFIED_UNRESOLVED = 'justified_unresolved',
	JUSTIFIED_RESOLVED_BY_CLEANER = 'justified_resolved_by_cleaner',
	JUSTIFIED_RESOLVED_BY_MANAGER = 'justified_resolved_by_manager',
}

export function translateComplaintStatus(status: COMPLAINT_STATUS, t: TFunction): string {
	switch (status) {
		case COMPLAINT_STATUS.UNJUSTIFIED:
			return t('pages:Reklamácia neoprávnená')
		case COMPLAINT_STATUS.JUSTIFIED_UNRESOLVED:
			return t('pages:Reklamácia oprávnená (nevyriešená)')
		case COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_CLEANER:
			return t('pages:Reklamácia oprávnená – vyriešená upratovačkou')
		case COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_MANAGER:
			return t('pages:Reklamácia oprávnená – vyriešená manažérom')
		default:
			return status
	}
}

export const translateComplaintStatusColor = (status: COMPLAINT_STATUS): string => {
	switch (status) {
		case COMPLAINT_STATUS.UNJUSTIFIED:
			return 'blue'
		case COMPLAINT_STATUS.JUSTIFIED_UNRESOLVED:
			return 'orange'
		case COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_CLEANER:
			return 'green'
		case COMPLAINT_STATUS.JUSTIFIED_RESOLVED_BY_MANAGER:
			return 'green'
		default:
			return 'gray'
	}
}

export interface IComplaintTableItem {
	id: number
	jobAddress: string
	cleanerName: string | null
	note: string | null
	status: COMPLAINT_STATUS
	createdAt: string
}

export interface IComplaintOriginalItem {
	id: number
	note: string | null
	finishID: number | null
	finishLabel: string | null
	status: COMPLAINT_STATUS
	createdAt: string
	job: { address: string }
	cleaner?: { id: number; name: string }
}

export interface IComplaintPayload {
	data: IComplaintOriginalItem | null
}

export interface IComplaintsPayload {
	tableData: IComplaintTableItem[]
	originalData: IComplaintOriginalItem[]
	pagination: IResponsePagination | null
}

interface IGetComplaintAction {
	type: LOAD_COMPLAINT
	payload: IComplaintPayload
}

interface IGetComplaintsAction {
	type: LOAD_SELECT_COMPLAINTS
	payload: IComplaintsPayload
}

export type IComplaintActions = IGetComplaintAction | IGetComplaintsAction

export interface IComplaintQueryParams {
	limit?: number | null
	page?: number | null
	search?: string | null
	startDate?: string | null
	endDate?: string | null
	users?: number[]
	jobs?: number[]
	orderBy?: string | null
	orderDirection?: string | null
}

export const getComplaints = (
	queryParams: IComplaintQueryParams,
	onSuccess: (data: IComplaintsPayload) => void = () => {}
): ThunkResult<Promise<IComplaintsPayload>> => async (dispatch) => {
	dispatch({ type: LOAD_SELECT_COMPLAINTS.START })

	try {
		const queries = mapValues(queryParams, (v) => v ?? undefined)
		const { data } = await getReq('/api/admin/complaints', queries)

		const originalData: IComplaintOriginalItem[] = data.complaints || []
		const tableData: IComplaintTableItem[] = map(originalData, (item) => ({
			id: item.id,
			jobAddress: item.job?.address || '-',
			note: item.note || '',
			cleanerName: item.cleaner ? item.cleaner.name : '',
			status: item.status,
			createdAt: item.createdAt
		}))

		const payload: IComplaintsPayload = {
			originalData,
			tableData,
			pagination: data.context || null
		}

		dispatch({ type: LOAD_SELECT_COMPLAINTS.DONE, payload })
		onSuccess(payload)
		return payload
	} catch (error) {
		dispatch({ type: LOAD_SELECT_COMPLAINTS.FAILED })
		return Promise.reject(error)
	}
}

export const getComplaint = (
	id: number,
	onSuccess: (data: IComplaintOriginalItem) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<IComplaintPayload>> => async (dispatch) => {
	dispatch({ type: LOAD_COMPLAINT.START })

	try {
		const { data } = await getReq(`/api/admin/complaints/${id}`)
		const payload: IComplaintPayload = { data }

		dispatch({ type: LOAD_COMPLAINT.DONE, payload })
		onSuccess(data)
		return payload
	} catch (error) {
		dispatch({ type: LOAD_COMPLAINT.FAILED })
		onFailure(error)
		return Promise.reject(error)
	}
}

export interface IComplaintForm {
	id: number | null
	note: string | null
	finishID: number | null
	finishLabel: string | null
	status: COMPLAINT_STATUS | null
	jobID: number | null
	cleanerID: number | null
}

export const createComplaint = (
	values: IComplaintForm,
	onSuccess: (id: number) => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<boolean>> => async (dispatch) => {
	dispatch({ type: CREATE_COMPLAINT.START })

	try {
		const normalizedValues = mapValues(values, (val) => val || undefined)
		const { data } = await postReq('/api/admin/complaints', null, normalizedValues)

		dispatch({ type: CREATE_COMPLAINT.DONE })
		onSuccess(get(data, 'data.id'))
		return true
	} catch (error) {
		dispatch({ type: CREATE_COMPLAINT.FAILED })
		onFailure(error)
		return false
	}
}

export const updateComplaint = (
	id: number,
	values: IComplaintForm,
	onSuccess: () => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<boolean>> => async (dispatch) => {
	dispatch({ type: CREATE_COMPLAINT.START })

	try {
		const normalizedValues = mapValues(values, (val) => val || undefined)
		await putReq(`/api/admin/complaints/${id}`, null, normalizedValues)
		dispatch({ type: CREATE_COMPLAINT.DONE })
		onSuccess()
		return true
	} catch (error) {
		dispatch({ type: CREATE_COMPLAINT.FAILED })
		onFailure(error)
		return false
	}
}

export const deleteComplaint = (
	id: number,
	onSuccess: () => void = () => {},
	onFailure: (error: any) => void = () => {}
): ThunkResult<Promise<boolean>> => async (dispatch) => {
	try {
		await deleteReq(`/api/admin/complaints/${id}`)
		onSuccess()
		return true
	} catch (error) {
		onFailure(error)
		return false
	}
}
